/**
 * MCP Server wrapper using the SDK
 */

import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { z } from "zod";
import { MemoryIntelligenceClient } from "../core/client.js";
import { MemoryIntelligenceConfig, ResponseFormat, MemoryType, QueryScope } from "../core/types.js";
import { formatResponse, truncateIfNeeded } from "../utils/formatting.js";
import { PredictiveRecallResult } from "../core/prediction-types.js";

const optionalContextFields = {
  organization_id: z.string().uuid().optional(),
  topic_id: z.string().optional(),
  query_scope: QueryScope.optional(),
};

const optionalCollectionContextFields = {
  ...optionalContextFields,
  memory_types: z.array(MemoryType).optional(),
};

export function createMCPServer(config: MemoryIntelligenceConfig): McpServer {
  const client = new MemoryIntelligenceClient(config);
  // Avoid massive generic inference across all tool registrations during
  // declaration emit; we return a strongly typed McpServer at the boundary.
  const server: any = new McpServer({
    name: "memory-intelligence-mcp-server",
    version: "1.1.0",
  });

  // Register analyze patterns tool
  server.registerTool(
    "memory_analyze_patterns",
    {
      title: "Analyze Memory Patterns",
      description: `Analyze usage patterns and trends in the user's memory collection.`,
      inputSchema: z.object({
        user_id: z.string().uuid(),
        time_range_days: z.number().int().min(1).max(365).default(30),
        ...optionalCollectionContextFields,
        response_format: z
          .enum([ResponseFormat.JSON, ResponseFormat.MARKDOWN])
          .default(ResponseFormat.MARKDOWN),
      }).strict(),
      annotations: {
        readOnlyHint: true,
        destructiveHint: false,
        idempotentHint: true,
        openWorldHint: true,
      },
    },
    async (rawParams: any) => {
      try {
        const params = {
          userId: rawParams.user_id,
          timeRangeDays: rawParams.time_range_days,
          organizationId: rawParams.organization_id,
          topicId: rawParams.topic_id,
          memoryTypes: rawParams.memory_types,
          queryScope: rawParams.query_scope,
          responseFormat: rawParams.response_format,
        };

        const response = await client.analyzePatterns(params);
        const analysis = response.data;
        const responseText = formatResponse(
          analysis,
          params.responseFormat || "markdown",
          (data) => {
            let md = `# Memory Pattern Analysis\n\n`;
            md += `**Time Range:** Last ${params.timeRangeDays || 30} days\n`;
            md += `**Total Memories:** ${data.total_memories}\n\n`;
            md += `## Distribution by Type\n`;
            for (const [type, count] of Object.entries(data.memories_by_type)) {
              const percentage = (((count as number) / data.total_memories) * 100).toFixed(1);
              md += `- **${type}**: ${count} (${percentage}%)\n`;
            }
            md += `\n## Activity Patterns\n`;
            md += `- **Peak Hours (UTC):** ${data.peak_creation_hours.join(", ")}\n`;
            md += `- **Daily Average:** ${data.creation_velocity.daily_average} memories/day\n`;
            md += `- **Trend:** ${data.creation_velocity.trend}\n`;
            md += `\n## Top Tags\n`;
            for (const { tag, count } of data.most_common_tags.slice(0, 5)) {
              md += `- **${tag}**: ${count} uses\n`;
            }
            md += `\n## AI Insights\n`;
            for (const insight of data.insights) {
              md += `${insight}\n`;
            }
            return md;
          }
        );

        return {
          content: [{ type: "text" as const, text: truncateIfNeeded(responseText) }],
        };
      } catch (error) {
        return {
          isError: true,
          content: [
            {
              type: "text" as const,
              text: `Error: ${error instanceof Error ? error.message : "Unknown error"}`,
            },
          ],
        };
      }
    }
  );

  // Register suggest tags tool
  server.registerTool(
    "memory_suggest_tags",
    {
      title: "Suggest Tags for Memory",
      description: `AI-powered tag suggestions for a memory.`,
      inputSchema: z.object({
        memory_id: z.string().uuid(),
        user_id: z.string().uuid(),
        max_suggestions: z.number().int().min(1).max(20).default(5),
        include_existing_tags: z.boolean().default(true),
        response_format: z
          .enum([ResponseFormat.JSON, ResponseFormat.MARKDOWN])
          .default(ResponseFormat.MARKDOWN),
      }).strict(),
      annotations: {
        readOnlyHint: true,
        destructiveHint: false,
        idempotentHint: false,
        openWorldHint: true,
      },
    },
    async (rawParams: any) => {
      try {
        const params = {
          memoryId: rawParams.memory_id,
          userId: rawParams.user_id,
          maxSuggestions: rawParams.max_suggestions,
          includeExistingTags: rawParams.include_existing_tags,
          responseFormat: rawParams.response_format,
        };

        const response = await client.suggestTags(params);
        const result = response.data;
        const responseText = formatResponse(
          result,
          params.responseFormat || "markdown",
          (data) => {
            let md = `# Tag Suggestions\n\n`;
            md += `**Existing Tags:** ${data.existing_tags.join(", ") || "none"}\n\n`;
            md += `## Suggested Tags\n`;
            for (const suggestion of data.suggestions) {
              md += `\n### \`${suggestion.tag}\`\n`;
              md += `**Confidence:** ${(suggestion.confidence * 100).toFixed(0)}%\n`;
              md += `**Reason:** ${suggestion.reasoning}\n`;
            }
            return md;
          }
        );

        return {
          content: [{ type: "text" as const, text: truncateIfNeeded(responseText) }],
        };
      } catch (error) {
        return {
          isError: true,
          content: [
            {
              type: "text" as const,
              text: `Error: ${error instanceof Error ? error.message : "Unknown error"}`,
            },
          ],
        };
      }
    }
  );

  // Register find related tool
  server.registerTool(
    "memory_find_related",
    {
      title: "Find Related Memories",
      description: `Find semantically related memories using vector similarity.`,
      inputSchema: z.object({
        memory_id: z.string().uuid(),
        user_id: z.string().uuid(),
        limit: z.number().int().min(1).max(50).default(10),
        similarity_threshold: z.number().min(0).max(1).default(0.7),
        ...optionalCollectionContextFields,
        response_format: z
          .enum([ResponseFormat.JSON, ResponseFormat.MARKDOWN])
          .default(ResponseFormat.MARKDOWN),
      }).strict(),
      annotations: {
        readOnlyHint: true,
        destructiveHint: false,
        idempotentHint: true,
        openWorldHint: true,
      },
    },
    async (rawParams: any) => {
      try {
        const params = {
          memoryId: rawParams.memory_id,
          userId: rawParams.user_id,
          limit: rawParams.limit,
          similarityThreshold: rawParams.similarity_threshold,
          organizationId: rawParams.organization_id,
          topicId: rawParams.topic_id,
          memoryTypes: rawParams.memory_types,
          queryScope: rawParams.query_scope,
          responseFormat: rawParams.response_format,
        };

        const response = await client.findRelated(params);
        const result = response.data;
        const responseText = formatResponse(
          result,
          params.responseFormat || "markdown",
          (data) => {
            let md = `# Related Memories\n\n`;
            md += `**Source:** ${data.source_memory.title}\n`;
            md += `**Found:** ${data.total_found} related memories\n\n`;
            for (const mem of data.related_memories) {
              md += `## ${mem.title}\n`;
              md += `**Similarity:** ${(mem.similarity_score * 100).toFixed(1)}%\n`;
              if (mem.shared_tags.length > 0) {
                md += `**Shared Tags:** ${mem.shared_tags.join(", ")}\n`;
              }
              md += `**Preview:** ${mem.content_preview}\n\n`;
            }
            return md;
          }
        );

        return {
          content: [{ type: "text" as const, text: truncateIfNeeded(responseText) }],
        };
      } catch (error) {
        return {
          isError: true,
          content: [
            {
              type: "text" as const,
              text: `Error: ${error instanceof Error ? error.message : "Unknown error"}`,
            },
          ],
        };
      }
    }
  );

  // Register detect duplicates tool
  server.registerTool(
    "memory_detect_duplicates",
    {
      title: "Detect Duplicate Memories",
      description: `Find potential duplicate memories.`,
      inputSchema: z.object({
        user_id: z.string().uuid(),
        similarity_threshold: z.number().min(0.8).max(0.99).default(0.9),
        max_pairs: z.number().int().min(1).max(100).default(20),
        ...optionalCollectionContextFields,
        response_format: z
          .enum([ResponseFormat.JSON, ResponseFormat.MARKDOWN])
          .default(ResponseFormat.MARKDOWN),
      }).strict(),
      annotations: {
        readOnlyHint: true,
        destructiveHint: false,
        idempotentHint: true,
        openWorldHint: true,
      },
    },
    async (rawParams: any) => {
      try {
        const params = {
          userId: rawParams.user_id,
          similarityThreshold: rawParams.similarity_threshold,
          maxPairs: rawParams.max_pairs,
          organizationId: rawParams.organization_id,
          topicId: rawParams.topic_id,
          memoryTypes: rawParams.memory_types,
          queryScope: rawParams.query_scope,
          responseFormat: rawParams.response_format,
        };

        const response = await client.detectDuplicates(params);
        const result = response.data;
        const responseText = formatResponse(
          result,
          params.responseFormat || "markdown",
          (data) => {
            let md = `# Duplicate Detection\n\n`;
            md += `**Memories Analyzed:** ${data.memories_analyzed}\n`;
            md += `**Duplicate Groups Found:** ${data.total_groups}\n`;
            md += `**Total Duplicates:** ${data.total_duplicates}\n`;
            md += `**Storage Savings:** ${data.potential_storage_savings}\n\n`;
            for (const group of data.duplicate_groups) {
              md += `### ${group.primary_title}\n`;
              md += `**Similarity Score:** ${(group.similarity_score * 100).toFixed(1)}%\n`;
              md += `**Duplicates:**\n`;
              for (const dup of group.duplicates) {
                md += `- ${dup.title} (${(dup.similarity * 100).toFixed(1)}% similar)\n`;
              }
              md += `\n`;
            }
            return md;
          }
        );

        return {
          content: [{ type: "text" as const, text: truncateIfNeeded(responseText) }],
        };
      } catch (error) {
        return {
          isError: true,
          content: [
            {
              type: "text" as const,
              text: `Error: ${error instanceof Error ? error.message : "Unknown error"}`,
            },
          ],
        };
      }
    }
  );

  // Register extract insights tool
  server.registerTool(
    "memory_extract_insights",
    {
      title: "Extract Insights from Memories",
      description: `Extract key insights and patterns from memories.`,
      inputSchema: z.object({
        user_id: z.string().uuid(),
        topic: z.string().optional(),
        memory_type: z.string().optional(),
        max_memories: z.number().int().min(5).max(100).default(20),
        ...optionalCollectionContextFields,
        response_format: z
          .enum([ResponseFormat.JSON, ResponseFormat.MARKDOWN])
          .default(ResponseFormat.MARKDOWN),
      }).strict(),
      annotations: {
        readOnlyHint: true,
        destructiveHint: false,
        idempotentHint: false,
        openWorldHint: true,
      },
    },
    async (rawParams: any) => {
      try {
        const params = {
          userId: rawParams.user_id,
          topic: rawParams.topic,
          memoryType: rawParams.memory_type,
          maxMemories: rawParams.max_memories,
          organizationId: rawParams.organization_id,
          topicId: rawParams.topic_id,
          memoryTypes: rawParams.memory_types,
          queryScope: rawParams.query_scope,
          responseFormat: rawParams.response_format,
        };

        const response = await client.extractInsights(params);
        const result = response.data;
        const responseText = formatResponse(
          result,
          params.responseFormat || "markdown",
          (data) => {
            let md = `# Memory Insights\n\n`;
            md += `**Memories Analyzed:** ${data.memories_analyzed}\n`;
            if (data.topic_filter) {
              md += `**Topic:** ${data.topic_filter}\n`;
            }
            md += `\n## Summary\n${data.overall_summary}\n\n`;
            md += `## Insights\n`;
            const typeIcons: Record<string, string> = {
              themes: "🎯",
              connections: "🔗",
              gaps: "⚠️",
              actions: "✅",
              summary: "📋",
            };
            for (const insight of data.insights) {
              const icon = typeIcons[insight.type] || "📌";
              md += `\n### ${icon} ${insight.type.charAt(0).toUpperCase() + insight.type.slice(1)}\n`;
              md += `${insight.content}\n`;
              md += `**Confidence:** ${(insight.confidence * 100).toFixed(0)}%\n`;
            }
            return md;
          }
        );

        return {
          content: [{ type: "text" as const, text: truncateIfNeeded(responseText) }],
        };
      } catch (error) {
        return {
          isError: true,
          content: [
            {
              type: "text" as const,
              text: `Error: ${error instanceof Error ? error.message : "Unknown error"}`,
            },
          ],
        };
      }
    }
  );

  // Register health check tool
  server.registerTool(
    "memory_health_check",
    {
      title: "Memory Health Check",
      description: `Analyze the health and organization quality of memories.`,
      inputSchema: z.object({
        user_id: z.string().uuid(),
        ...optionalCollectionContextFields,
        response_format: z
          .enum([ResponseFormat.JSON, ResponseFormat.MARKDOWN])
          .default(ResponseFormat.MARKDOWN),
      }).strict(),
      annotations: {
        readOnlyHint: true,
        destructiveHint: false,
        idempotentHint: true,
        openWorldHint: true,
      },
    },
    async (rawParams: any) => {
      try {
        const params = {
          userId: rawParams.user_id,
          organizationId: rawParams.organization_id,
          topicId: rawParams.topic_id,
          memoryTypes: rawParams.memory_types,
          queryScope: rawParams.query_scope,
          responseFormat: rawParams.response_format,
        };

        const response = await client.healthCheck(params);
        const result = response.data;
        const responseText = formatResponse(
          result,
          params.responseFormat || "markdown",
          (data) => {
            let md = `# Memory Health Check\n\n`;
            md += `**Health Score:** ${data.health_score.overall}/100\n`;
            md += `**Status:** ${data.status}\n\n`;
            md += `## Score Breakdown\n`;
            md += `- **Organization:** ${data.health_score.breakdown.organization}\n`;
            md += `- **Tagging:** ${data.health_score.breakdown.tagging}\n`;
            md += `- **Recency:** ${data.health_score.breakdown.recency}\n`;
            md += `- **Completeness:** ${data.health_score.breakdown.completeness}\n`;
            md += `- **Diversity:** ${data.health_score.breakdown.diversity}\n\n`;
            md += `## Statistics\n`;
            md += `- **Total Memories:** ${data.statistics.total_memories}\n`;
            md += `- **Active Memories:** ${data.statistics.active_memories}\n`;
            md += `- **Memories with Tags:** ${data.statistics.memories_with_tags}\n`;
            md += `- **Unique Tags:** ${data.statistics.unique_tags}\n`;
            md += `- **Recent (30d):** ${data.statistics.recent_memories_30d}\n\n`;
            if (data.issues.length > 0) {
              md += `## Issues\n`;
              for (const issue of data.issues) {
                const icon = issue.severity === "high" ? "🔴" : issue.severity === "medium" ? "🟡" : "🟢";
                md += `- ${icon} **${issue.category}:** ${issue.description}\n`;
              }
              md += `\n`;
            }
            md += `## Recommendations\n`;
            for (const rec of data.recommendations) {
              md += `- ${rec}\n`;
            }
            return md;
          }
        );

        return {
          content: [{ type: "text" as const, text: truncateIfNeeded(responseText) }],
        };
      } catch (error) {
        return {
          isError: true,
          content: [
            {
              type: "text" as const,
              text: `Error: ${error instanceof Error ? error.message : "Unknown error"}`,
            },
          ],
        };
      }
    }
  );

  // Register predictive recall tool
  server.registerTool(
    "memory_predictive_recall",
    {
      title: "Predictive Memory Recall",
      description: `AI-powered prediction of memories you'll need based on your current context. Uses semantic similarity, temporal relevance, usage patterns, and serendipity scoring to anticipate what you need before you realize it.`,
      inputSchema: z.object({
        user_id: z.string().uuid(),
        ...optionalContextFields,
        context: z.object({
          current_project: z.string().optional().describe("Current project or task description"),
          recent_topics: z.array(z.string()).optional().describe("Recent topics you've been working on"),
          active_files: z.array(z.string()).optional().describe("Files currently being worked on"),
          context_text: z.string().optional().describe("Free-form context (current task, meeting notes, etc.)"),
          team_context: z.string().optional().describe("Team discussion topics for team-aware predictions"),
        }).describe("Your current work context for generating predictions"),
        limit: z.number().int().min(1).max(20).default(5).describe("Maximum predictions to return"),
        min_confidence: z.number().min(0).max(100).default(40).describe("Minimum confidence threshold (0-100)"),
        include_serendipity: z.boolean().default(true).describe("Include surprising adjacent discoveries"),
        memory_types: z.array(MemoryType).optional().describe("Filter by memory types"),
        time_window_days: z.number().int().min(1).max(365).default(90).describe("Time window for recency scoring"),
        response_format: z
          .enum([ResponseFormat.JSON, ResponseFormat.MARKDOWN])
          .default(ResponseFormat.MARKDOWN),
      }).strict(),
      annotations: {
        readOnlyHint: true,
        destructiveHint: false,
        idempotentHint: false,
        openWorldHint: true,
      },
    },
    async (rawParams: any) => {
      try {
        const params = {
          userId: rawParams.user_id,
          organizationId: rawParams.organization_id,
          topicId: rawParams.topic_id,
          queryScope: rawParams.query_scope,
          context: {
            currentProject: rawParams.context?.current_project,
            recentTopics: rawParams.context?.recent_topics,
            activeFiles: rawParams.context?.active_files,
            contextText: rawParams.context?.context_text,
            teamContext: rawParams.context?.team_context,
          },
          limit: rawParams.limit,
          minConfidence: rawParams.min_confidence,
          includeSerendipity: rawParams.include_serendipity,
          memoryTypes: rawParams.memory_types,
          timeWindowDays: rawParams.time_window_days,
          responseFormat: rawParams.response_format,
        };

        const response = await client.predictiveRecall(params);
        const result = response.data;
        const responseText = formatResponse(
          result,
          params.responseFormat || "markdown",
          (data: PredictiveRecallResult) => {
            let md = `# Predicted Memories for You\n\n`;
            md += `**Memories Analyzed:** ${data.memoriesAnalyzed}\n`;
            md += `**Predictions Found:** ${data.totalPredictions}\n\n`;

            if (data.predictions.length === 0) {
              md += `_No predictions above the confidence threshold. Try providing more context or lowering the threshold._\n`;
              return md;
            }

            md += `## Your Predictions\n\n`;

            for (const pred of data.predictions) {
              const confidenceIcon =
                pred.confidence >= 80 ? "🎯" :
                pred.confidence >= 60 ? "✨" :
                pred.confidence >= 40 ? "💡" : "🔍";

              const actionIcon =
                pred.suggestedAction === "apply" ? "⚡" :
                pred.suggestedAction === "reference" ? "📚" :
                pred.suggestedAction === "explore" ? "🔭" : "👀";

              md += `### ${confidenceIcon} ${pred.title}\n`;
              md += `**Confidence:** ${pred.confidence}% | **Type:** ${pred.type}\n`;
              md += `**Why:** ${pred.reason}\n`;
              md += `**Suggested Action:** ${actionIcon} ${pred.suggestedAction}\n`;

              if (pred.tags.length > 0) {
                md += `**Tags:** ${pred.tags.slice(0, 5).join(", ")}\n`;
              }

              md += `\n> ${pred.contentPreview}\n\n`;

              // Score breakdown
              md += `<details>\n<summary>Score Breakdown</summary>\n\n`;
              md += `- Semantic: ${pred.scoreBreakdown.semanticScore}%\n`;
              md += `- Temporal: ${pred.scoreBreakdown.temporalScore}%\n`;
              md += `- Frequency: ${pred.scoreBreakdown.frequencyScore}%\n`;
              md += `- Serendipity: ${pred.scoreBreakdown.serendipityScore}%\n`;
              md += `- **Combined:** ${pred.scoreBreakdown.combinedScore}%\n`;
              md += `</details>\n\n`;
              md += `---\n\n`;
            }

            md += `## Algorithm Info\n`;
            md += `- **Version:** ${data.algorithmInfo.version}\n`;
            md += `- **Embedding Model:** ${data.algorithmInfo.embeddingModel}\n`;
            md += `- **Time Window:** ${data.algorithmInfo.timeWindowDays} days\n`;
            md += `- **Weights:** Semantic ${data.scoringWeights.semantic * 100}%, `;
            md += `Temporal ${data.scoringWeights.temporal * 100}%, `;
            md += `Frequency ${data.scoringWeights.frequency * 100}%, `;
            md += `Serendipity ${data.scoringWeights.serendipity * 100}%\n`;

            return md;
          }
        );

        return {
          content: [{ type: "text" as const, text: truncateIfNeeded(responseText) }],
        };
      } catch (error) {
        return {
          isError: true,
          content: [
            {
              type: "text" as const,
              text: `Error: ${error instanceof Error ? error.message : "Unknown error"}`,
            },
          ],
        };
      }
    }
  );

  // Register prediction feedback tool
  server.registerTool(
    "memory_prediction_feedback",
    {
      title: "Record Prediction Feedback",
      description: `Record whether a prediction was useful. This helps improve future predictions.`,
      inputSchema: z.object({
        memory_id: z.string().uuid(),
        user_id: z.string().uuid(),
        useful: z.boolean().describe("Was this prediction useful?"),
        action: z.enum(["clicked", "saved", "dismissed", "ignored"]).describe("What action was taken"),
        dismiss_reason: z.enum(["not_relevant", "already_know", "not_now", "other"]).optional().describe("Reason for dismissal"),
      }).strict(),
      annotations: {
        readOnlyHint: false,
        destructiveHint: false,
        idempotentHint: true,
        openWorldHint: false,
      },
    },
    async (rawParams: any) => {
      try {
        await client.recordPredictionFeedback({
          memoryId: rawParams.memory_id,
          userId: rawParams.user_id,
          useful: rawParams.useful,
          action: rawParams.action,
          dismissReason: rawParams.dismiss_reason,
        });

        return {
          content: [{
            type: "text" as const,
            text: `Feedback recorded. Thank you for helping improve predictions!`,
          }],
        };
      } catch (error) {
        return {
          isError: true,
          content: [
            {
              type: "text" as const,
              text: `Error: ${error instanceof Error ? error.message : "Unknown error"}`,
            },
          ],
        };
      }
    }
  );

  // Register behavior record tool
  server.registerTool(
    "behavior_record",
    {
      title: "Record Behavior Pattern",
      description: `Record a successful workflow pattern for future recall.`,
      inputSchema: z.object({
        user_id: z.string().uuid(),
        trigger: z.string().min(1, "Trigger description is required"),
        context: z.object({
          directory: z.string(),
          project_type: z.string().optional(),
          branch: z.string().optional(),
          files_touched: z.array(z.string()).optional(),
        }).optional(),
        actions: z.array(z.object({
          tool: z.string(),
          parameters: z.record(z.unknown()).optional(),
          outcome: z.enum(["success", "partial", "failed"]),
          timestamp: z.string().optional(),
          duration_ms: z.number().optional(),
        })).min(1, "At least one action is required"),
        final_outcome: z.enum(["success", "partial", "failed"]),
        confidence: z.number().min(0).max(1).default(0.7),
      }).strict(),
      annotations: {
        readOnlyHint: false,
        destructiveHint: false,
        idempotentHint: true,
        openWorldHint: false,
      },
    },
    async (rawParams: any) => {
      try {
        const response = await client.recordBehavior({
          user_id: rawParams.user_id,
          trigger: rawParams.trigger,
          context: rawParams.context || {},
          actions: rawParams.actions,
          final_outcome: rawParams.final_outcome,
          confidence: rawParams.confidence,
        });

        return {
          content: [{
            type: "text" as const,
            text: `Behavior pattern recorded: ${response.data.message}\nPattern ID: ${response.data.pattern.id}\nWas duplicate: ${response.data.was_duplicate}`,
          }],
        };
      } catch (error) {
        return {
          isError: true,
          content: [
            {
              type: "text" as const,
              text: `Error: ${error instanceof Error ? error.message : "Unknown error"}`,
            },
          ],
        };
      }
    }
  );

  // Register behavior recall tool
  server.registerTool(
    "behavior_recall",
    {
      title: "Recall Behavior Patterns",
      description: `Recall similar behavior patterns based on current context.`,
      inputSchema: z.object({
        user_id: z.string().uuid(),
        context: z.object({
          current_task: z.string().min(1, "Current task description is required"),
          directory: z.string().optional(),
          project_type: z.string().optional(),
        }),
        limit: z.number().int().min(1).max(20).default(5),
        similarity_threshold: z.number().min(0).max(1).default(0.7),
      }).strict(),
      annotations: {
        readOnlyHint: true,
        destructiveHint: false,
        idempotentHint: true,
        openWorldHint: true,
      },
    },
    async (rawParams: any) => {
      try {
        const response = await client.recallBehavior({
          user_id: rawParams.user_id,
          context: rawParams.context,
          limit: rawParams.limit,
          similarity_threshold: rawParams.similarity_threshold,
        });

        const patterns = response.data?.patterns || [];
        let text = `Found ${patterns.length} behavior patterns:\n\n`;
        
        patterns.forEach((pattern, i) => {
          text += `${i + 1}. "${pattern.pattern.trigger}" (${Math.round(pattern.similarity_score * 100)}% similar)\n`;
          text += `   Actions: ${pattern.pattern.actions.map((a: any) => a.tool).join(" → ")}\n`;
          text += `   Outcome: ${pattern.pattern.final_outcome}\n\n`;
        });

        return {
          content: [{ type: "text" as const, text }],
        };
      } catch (error) {
        return {
          isError: true,
          content: [
            {
              type: "text" as const,
              text: `Error: ${error instanceof Error ? error.message : "Unknown error"}`,
            },
          ],
        };
      }
    }
  );

  // Register behavior suggest tool
  server.registerTool(
    "behavior_suggest",
    {
      title: "Suggest Next Actions",
      description: `Get AI-powered action suggestions based on learned patterns.`,
      inputSchema: z.object({
        user_id: z.string().uuid(),
        current_state: z.object({
          task_description: z.string().min(1, "Task description is required"),
          completed_steps: z.array(
            z.union([
              z.string().min(1, "Completed step must be a non-empty string"),
              z.object({
                tool: z.string().min(1, "Legacy step objects must include tool"),
                params: z.record(z.unknown()).optional(),
                parameters: z.record(z.unknown()).optional(),
              }).transform((step) => step.tool),
            ])
          ).optional(),
        }),
        max_suggestions: z.number().int().min(1).max(10).default(3),
      }).strict(),
      annotations: {
        readOnlyHint: true,
        destructiveHint: false,
        idempotentHint: true,
        openWorldHint: true,
      },
    },
    async (rawParams: any) => {
      try {
        const response = await client.suggestAction({
          user_id: rawParams.user_id,
          current_state: rawParams.current_state,
          max_suggestions: rawParams.max_suggestions,
        });

        const suggestions = response.data?.suggestions || [];
        let text = `Suggested actions:\n\n`;
        
        suggestions.forEach((suggestion, i) => {
          text += `${i + 1}. ${suggestion.action}\n`;
          text += `   Tool: ${suggestion.tool}\n`;
          text += `   Confidence: ${Math.round(suggestion.confidence * 100)}%\n`;
          text += `   Reasoning: ${suggestion.reasoning}\n\n`;
        });

        return {
          content: [{ type: "text" as const, text }],
        };
      } catch (error) {
        return {
          isError: true,
          content: [
            {
              type: "text" as const,
              text: `Error: ${error instanceof Error ? error.message : "Unknown error"}`,
            },
          ],
        };
      }
    }
  );

  return server as McpServer;
}
